// LocalStorage-backed store for words and study sessions.
// Words have a 4-category spaced-repetition state:
//   - 'new'     : first time seen           → review every ~10 days  (≈3/month)
//   - 'unknown' : "Bilmiyorum"              → review every ~2 days   (≈4/week)
//   - 'review'  : "Tekrar Edeyim"           → review every ~4 days   (≈6-8/month)
//   - 'learned' : "Öğrendim"                → review every ~20 days  (≈1-2/month)

import { useCallback, useEffect, useState } from 'react';
import { SEED_WORDS, SEED_VERSION } from '../data/seedWords';

const WORDS_KEY = 'dtflash.words.v1';
const SESSIONS_KEY = 'dtflash.sessions.v1';
const SEED_FLAG_KEY = 'dtflash.seed.version';

export const CATEGORIES = ['new', 'unknown', 'review', 'learned'];

export const CATEGORY_INTERVAL_DAYS = {
  learned: 20,  // ayda 1-2 tekrar
  review: 8,    // "Kolay" — ayda 3-4 tekrar
  new: 5,       // İlk defa gördüm — ayda 5-6 tekrar
  unknown: 4,   // "Zor" — ayda 7-8 tekrar
};

export const CATEGORY_LABEL = {
  new: 'İlk Defa Gördüm',
  unknown: 'Zor',
  review: 'Kolay',
  learned: 'Öğrendim',
};

export const CATEGORY_TONE = {
  new: 'orange',
  unknown: 'rose',
  review: 'sky',
  learned: 'emerald',
};

// ---------- Storage helpers ------------------------------------------------
const readJson = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (k, v) => {
  localStorage.setItem(k, JSON.stringify(v));
  window.dispatchEvent(new CustomEvent('dtflash:update', { detail: { key: k } }));
};

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36));

const nowISO = () => new Date().toISOString();

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// ---------- Seeding --------------------------------------------------------
/**
 * Auto-seed the local store with the bundled B2 vocabulary the first time the
 * app loads (or after a seed-version bump). Existing words (matched by german
 * text, case-insensitive) are kept untouched; only missing ones are added.
 */
function ensureSeeded() {
  try {
    const seededVersion = parseInt(localStorage.getItem(SEED_FLAG_KEY) || '0', 10);
    if (seededVersion >= SEED_VERSION) return;

    const existing = readJson(WORDS_KEY, []);
    const present = new Set(existing.map((w) => w.german.toLowerCase()));
    const now = nowISO();
    const additions = SEED_WORDS
      .filter((s) => !present.has(s.german.toLowerCase()))
      .map((s) => ({
        id: uid(),
        german: s.german,
        type: s.type,
        turkish: null,
        article: null,
        plural: null,
        sentence: null,
        sentences: null,
        praesens: null,
        praeteritum: null,
        perfekt: null,
        createdAt: now,
        category: 'new',
        lastSeen: null,
        nextDue: now,
        knownCount: 0,
        unknownCount: 0,
        loading: false,
        seeded: true,
      }));
    if (additions.length) {
      writeJson(WORDS_KEY, [...additions, ...existing]);
    }
    localStorage.setItem(SEED_FLAG_KEY, String(SEED_VERSION));
  } catch (e) {
    console.error('seed failed', e);
  }
}
ensureSeeded();

// ---------- Word API -------------------------------------------------------
function normaliseWord(payload) {
  const now = nowISO();
  return {
    id: uid(),
    createdAt: now,
    category: 'new',
    lastSeen: null,
    nextDue: now,
    knownCount: 0,
    unknownCount: 0,
    loading: false,
    turkish: null,
    article: null,
    plural: null,
    sentence: null,
    sentences: null,
    praesens: null,
    praeteritum: null,
    perfekt: null,
    ...payload,
  };
}

export const wordsApi = {
  all: () => readJson(WORDS_KEY, []),
  add: (payload) => {
    const list = readJson(WORDS_KEY, []);
    const item = normaliseWord(payload);
    writeJson(WORDS_KEY, [item, ...list]);
    return item;
  },
  addMany: (payloads) => {
    const list = readJson(WORDS_KEY, []);
    const items = payloads.map((p) => normaliseWord(p));
    writeJson(WORDS_KEY, [...items, ...list]);
    return items;
  },
  update: (id, patch) => {
    const list = readJson(WORDS_KEY, []);
    const next = list.map((w) => (w.id === id ? { ...w, ...patch } : w));
    writeJson(WORDS_KEY, next);
  },
  remove: (id) => {
    const list = readJson(WORDS_KEY, []);
    writeJson(WORDS_KEY, list.filter((w) => w.id !== id));
  },
  clearAll: () => {
    writeJson(WORDS_KEY, []);
    // Also reset seed so user can reload the bundled set
    localStorage.removeItem(SEED_FLAG_KEY);
  },
  reseed: () => {
    localStorage.removeItem(SEED_FLAG_KEY);
    ensureSeeded();
  },
  /**
   * Apply a study judgement. Updates category, nextDue, counts.
   */
  judge: (id, category) => {
    const list = readJson(WORDS_KEY, []);
    const now = new Date();
    const interval = CATEGORY_INTERVAL_DAYS[category] || 10;
    const next = list.map((w) => {
      if (w.id !== id) return w;
      const known = category === 'learned' || category === 'review';
      return {
        ...w,
        category,
        lastSeen: now.toISOString(),
        nextDue: addDays(now, interval).toISOString(),
        knownCount: w.knownCount + (known ? 1 : 0),
        unknownCount: w.unknownCount + (known ? 0 : 1),
      };
    });
    writeJson(WORDS_KEY, next);
  },
};

// ---------- Sessions -------------------------------------------------------
export const sessionsApi = {
  all: () => readJson(SESSIONS_KEY, []),
  record: (entry) => {
    const list = readJson(SESSIONS_KEY, []);
    const item = { id: uid(), at: nowISO(), ...entry };
    writeJson(SESSIONS_KEY, [item, ...list].slice(0, 500));
    return item;
  },
  clear: () => writeJson(SESSIONS_KEY, []),
};

// ---------- React hook -----------------------------------------------------
export function useStore() {
  const [words, setWords] = useState(() => wordsApi.all());
  const [sessions, setSessions] = useState(() => sessionsApi.all());

  useEffect(() => {
    const refresh = () => {
      setWords(wordsApi.all());
      setSessions(sessionsApi.all());
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('dtflash:update', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('dtflash:update', refresh);
    };
  }, []);

  const refresh = useCallback(() => {
    setWords(wordsApi.all());
    setSessions(sessionsApi.all());
  }, []);

  return { words, sessions, refresh };
}

// ---------- Deck builders --------------------------------------------------
/**
 * Returns a study deck for a session:
 *   - only words whose nextDue ≤ now (due)
 *   - fully shuffled (so the alphabetical seed order never dominates)
 *   - capped at `limit` cards
 */
export function buildDueDeck(words, limit = 25) {
  const now = Date.now();
  const due = words.filter((w) => !w.nextDue || new Date(w.nextDue).getTime() <= now);
  // Full Fisher-Yates shuffle — no bias toward A-B words even when nextDue ties
  for (let i = due.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [due[i], due[j]] = [due[j], due[i]];
  }
  return due.slice(0, limit);
}

export function isStub(w) {
  // A "stub" has no Turkish translation yet — needs AI enrichment
  return !w.turkish;
}
